import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
    static values = {
        reorderUrl: String,
        selectedSovUrl: String,
        selectedSovContainerId: String
    }

    connect() {
        this.debugEnabled = true

        this.log("CONNECT", "EstimatesTable controller connected")
        this.log("CONNECT", "Element:", this.element)
        this.log("CONNECT", "reorderUrlValue:", this.reorderUrlValue, "hasReorderUrlValue:", this.hasReorderUrlValue)

        // Sanity checks
        this.log("ENV", "Turbo present:", !!window.Turbo, "Stimulus present:", !!window.Stimulus)
        this.log("ENV", "DataTable present:", !!window.DataTable, "jQuery present:", !!window.$)

        // Global event taps (scoped to this.element via capturing listener)
        this.attachDebugEventTaps()

        this.initDataTable()
        this.initSortable()
    }

    disconnect() {
        this.log("DISCONNECT", "Controller disconnected")
        this.detachDebugEventTaps()
    }

    // ============================================================
    // AUTO-SAVE
    // ============================================================

    submit(event) {
        this.logGroupStart("AUTOSAVE submit()", event)

        const target = event?.target
        if (!target) {
            this.warn("AUTOSAVE", "No event.target. Aborting.")
            this.logGroupEnd()
            return
        }

        this.log("AUTOSAVE", "Target:", target)
        this.log("AUTOSAVE", "Target tag:", target.tagName, "name:", target.name, "value:", target.value)
        this.log("AUTOSAVE", "Target classes:", [...target.classList].join(" "))

        // Confirm the action attribute exists (useful when Turbo frames replace rows)
        this.log("AUTOSAVE", "Target dataset.action:", target.dataset?.action)

        const form = target.closest("form")
        if (!form) {
            this.error("AUTOSAVE", "No parent <form> found via closest('form'). This means the input is NOT inside the form in the DOM.")
            this.log("AUTOSAVE", "Closest turbo-frame:", target.closest("turbo-frame"))
            this.logGroupEnd()
            return
        }

        this.log("AUTOSAVE", "Found form:", form)
        this.log("AUTOSAVE", "Form id:", form.id, "action:", form.action, "method:", form.method)
        this.log("AUTOSAVE", "Form data-turbo:", form.dataset?.turbo, "data-turbo-stream:", form.dataset?.turboStream)

        // If the form is inside a turbo-frame, log it
        const frame = form.closest("turbo-frame")
        this.log("AUTOSAVE", "Form turbo-frame:", frame ? (frame.id || "(no id)") : "none")

        // Attach one-time listeners for this specific submission so you can see whether it actually fires
        this.attachOneTimeFormLifecycleLogs(form)

        // Submit
        try {
            if (typeof form.requestSubmit === "function") {
                this.log("AUTOSAVE", "Submitting via form.requestSubmit()")
                form.requestSubmit()
            } else {
                this.warn("AUTOSAVE", "requestSubmit() not available; falling back to form.submit()")
                form.submit()
            }
        } catch (e) {
            this.error("AUTOSAVE", "Submission threw:", e)
        }

        this.logGroupEnd()
    }

    openSelectedSov(event) {
        event.preventDefault()

        if (!this.hasSelectedSovUrlValue || !this.hasSelectedSovContainerIdValue) {
            this.warn("SOV", "Selected SOV URL/container values are missing.")
            return
        }

        const estimateIds = this.selectedEstimateIds()
        if (estimateIds.length === 0) {
            window.alert("Select at least one estimate to build an SOV from the selection.")
            return
        }

        const url = new URL(this.selectedSovUrlValue, window.location.origin)
        estimateIds.forEach((estimateId) => url.searchParams.append("estimate_ids[]", estimateId))

        fetch(url.toString(), {
            method: "GET",
            headers: {
                "Accept": "text/html",
                "X-Requested-With": "XMLHttpRequest"
            }
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load selected-estimate SOV modal (${response.status})`)
                }

                return response.text()
            })
            .then((html) => {
                const container = document.getElementById(this.selectedSovContainerIdValue)
                if (!container) {
                    throw new Error(`Missing selected SOV modal container #${this.selectedSovContainerIdValue}`)
                }

                container.innerHTML = html

                const modalEl = container.querySelector(".modal")
                if (!modalEl) {
                    throw new Error("Selected-estimate SOV modal markup was not returned.")
                }

                modalEl.addEventListener("hidden.bs.modal", () => {
                    container.innerHTML = ""
                }, { once: true })

                window.bootstrap.Modal.getOrCreateInstance(modalEl).show()
            })
            .catch((error) => {
                this.error("SOV", "Unable to open selected-estimate SOV modal:", error)
                window.alert("Unable to load the SOV preview for the selected estimates.")
            })
    }

    // ============================================================
    // SORTABLE
    // ============================================================

    initSortable() {
        const tbody = this.element.querySelector("tbody")
        if (!tbody) {
            this.warn("SORTABLE", "No <tbody> found under controller element. Sortable not initialized.")
            return
        }
        if (tbody.dataset.sortableInitialized) {
            this.log("SORTABLE", "Already initialized; skipping.")
            return
        }

        tbody.dataset.sortableInitialized = "true"
        this.log("SORTABLE", "Initializing Sortable on tbody:", tbody)

        Sortable.create(tbody, {
            handle: ".drag-handle",
            animation: 120,
            ghostClass: "sortable-ghost",
            onEnd: () => {
                this.logGroupStart("SORTABLE onEnd()", null)

                if (!this.hasReorderUrlValue) {
                    this.warn("SORTABLE", "No reorderUrlValue; skipping reorder PATCH.")
                    this.logGroupEnd()
                    return
                }

                const ids = [...tbody.querySelectorAll("tr")]
                    .map(row => row.dataset.estimateId)
                    .filter(Boolean)

                this.log("SORTABLE", "New order ids:", ids)
                this.log("SORTABLE", "PATCH ->", this.reorderUrlValue)

                fetch(this.reorderUrlValue, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
                    },
                    body: JSON.stringify({ order: ids })
                })
                    .then(async (resp) => {
                        this.log("SORTABLE", "Reorder response status:", resp.status)
                        const text = await resp.text().catch(() => "")
                        this.log("SORTABLE", "Reorder response body (first 400):", text.slice(0, 400))
                    })
                    .catch((err) => {
                        this.error("SORTABLE", "Reorder fetch failed:", err)
                    })
                    .finally(() => this.logGroupEnd())
            }
        })
    }

    // ============================================================
    // DATATABLE
    // ============================================================

    initDataTable() {
        const table = this.element.querySelector("table")
        if (!table) {
            this.warn("DATATABLE", "No <table> found under controller element. DataTables not initialized.")
            return
        }

        const hasDT = !!window.DataTable
        const hasJQ = !!window.$ && !!window.$.fn
        const hasJQDT = hasJQ && !!window.$.fn.DataTable

        this.log("DATATABLE", "table:", table)
        this.log("DATATABLE", "DataTable global:", hasDT, "jQuery:", hasJQ, "jQuery.DataTable:", hasJQDT)

        // Your existing guard
        if (window.DataTable && window.$?.fn?.DataTable && !$.fn.DataTable.isDataTable(table)) {
            this.log("DATATABLE", "Initializing DataTable()")
            new DataTable(table, {
                dom: "t",
                paging: false,
                searching: true,
                ordering: true,
                info: false,
                autoWidth: false,
                columnDefs: [{ orderable: false, targets: [0, 1, 10] }]
            })
        } else {
            this.log("DATATABLE", "Skipping initialization (missing libs or already initialized).")
        }
    }

    selectedEstimateIds() {
        return [...this.element.querySelectorAll('input[name="estimate_ids[]"]:checked')]
            .map((checkbox) => checkbox.value)
            .filter(Boolean)
    }

    // ============================================================
    // DEBUG HELPERS
    // ============================================================

    attachDebugEventTaps() {
        // Capture events at the container level so you can see if the events fire at all
        this._debugTap = (e) => {
            const t = e.target
            if (!t) return

            // Only log autosave elements to avoid noise
            const isAutoSave = t.classList && t.classList.contains("auto-save")
            if (!isAutoSave) return

            this.log("EVENT", `${e.type} captured on .auto-save`, {
                tag: t.tagName,
                name: t.name,
                value: t.value,
                action: t.dataset?.action,
                form: t.closest("form")?.id
            })
        }

        this.element.addEventListener("change", this._debugTap, true)
        this.element.addEventListener("blur", this._debugTap, true) // blur does not bubble; capture will see it
        this.element.addEventListener("focusout", this._debugTap, true)
        this.element.addEventListener("keydown", this._debugTap, true)

        // Turbo lifecycle events (global)
        this._turboBeforeFetch = (e) => this.log("TURBO", "turbo:before-fetch-request", e.detail)
        this._turboBeforeFetchResp = (e) => this.log("TURBO", "turbo:before-fetch-response", e.detail)
        this._turboSubmitStart = (e) => this.log("TURBO", "turbo:submit-start", e.detail)
        this._turboSubmitEnd = (e) => this.log("TURBO", "turbo:submit-end", e.detail)

        document.addEventListener("turbo:before-fetch-request", this._turboBeforeFetch)
        document.addEventListener("turbo:before-fetch-response", this._turboBeforeFetchResp)
        document.addEventListener("turbo:submit-start", this._turboSubmitStart)
        document.addEventListener("turbo:submit-end", this._turboSubmitEnd)

        this.log("DEBUG", "Attached event taps (change/blur/focusout/keydown + Turbo lifecycle)")
    }

    detachDebugEventTaps() {
        if (this._debugTap) {
            this.element.removeEventListener("change", this._debugTap, true)
            this.element.removeEventListener("blur", this._debugTap, true)
            this.element.removeEventListener("focusout", this._debugTap, true)
            this.element.removeEventListener("keydown", this._debugTap, true)
            this._debugTap = null
        }

        if (this._turboBeforeFetch) document.removeEventListener("turbo:before-fetch-request", this._turboBeforeFetch)
        if (this._turboBeforeFetchResp) document.removeEventListener("turbo:before-fetch-response", this._turboBeforeFetchResp)
        if (this._turboSubmitStart) document.removeEventListener("turbo:submit-start", this._turboSubmitStart)
        if (this._turboSubmitEnd) document.removeEventListener("turbo:submit-end", this._turboSubmitEnd)

        this._turboBeforeFetch = null
        this._turboBeforeFetchResp = null
        this._turboSubmitStart = null
        this._turboSubmitEnd = null
    }

    attachOneTimeFormLifecycleLogs(form) {
        // Logs whether the form actually emits submit, and whether Turbo intercepts it
        const onSubmit = (e) => this.log("FORM", `submit event fired for #${form.id}`, { defaultPrevented: e.defaultPrevented })
        form.addEventListener("submit", onSubmit, { once: true })
    }

    logGroupStart(label, event) {
        if (!this.debugEnabled) return
        console.groupCollapsed(`[%cestimates-table%c] ${label}`, "color: #0b5", "color: inherit")
        if (event) this.log("EVENT", event)
    }

    logGroupEnd() {
        if (!this.debugEnabled) return
        console.groupEnd()
    }

    log(scope, ...args) {
        if (!this.debugEnabled) return
        console.log(`[%cestimates-table%c] ${scope}:`, "color: #0b5", "color: inherit", ...args)
    }

    warn(scope, ...args) {
        if (!this.debugEnabled) return
        console.warn(`[%cestimates-table%c] ${scope}:`, "color: #0b5", "color: inherit", ...args)
    }

    error(scope, ...args) {
        if (!this.debugEnabled) return
        console.error(`[%cestimates-table%c] ${scope}:`, "color: #0b5", "color: inherit", ...args)
    }
}
